// Steuerpaket S1: liest das Immoware-Buchungsjournal (CSV, Semikolon, deutsche Zahlen/Datumsangaben).
// Immoware liefert je nach Export ISO-8859-1 oder UTF-8 – beides wird erkannt.
import { brauchtWindows1252 } from '../eigentuemer/csvImport';

export interface JournalZeile {
  zeile: number;
  nummer: string;
  text: string;
  buchungstyp: string;
  beleg_nr: string;
  buchungsdatum: string | null;
  wertstellung: string | null;
  abgrenzung: string | null;
  brutto: number;
  netto: number | null;
  ust_satz: number | null;
  soll_konto_nr: string; soll_konto: string; soll_kontoart: string; soll_kategorie: string;
  haben_konto_nr: string; haben_konto: string; haben_kontoart: string; haben_kategorie: string;
  op: boolean | null;
  op_betrag: number | null;
}

export interface JournalErgebnis {
  zeilen: JournalZeile[];
  fehler: string[];
  /** Bankkonten in der Datei – zur Kontrolle, ob die Datei zum Objekt passt */
  bankkonten: string[];
  von: string | null;
  bis: string | null;
}

/** Spalte im Journal → Feld; Pflichtspalten müssen vorhanden sein. */
const SPALTEN: Record<string, keyof JournalZeile> = {
  'Nummer': 'nummer', 'Text': 'text', 'Buchungstyp': 'buchungstyp', 'Beleg-Nr.': 'beleg_nr',
  'Buchungsdatum': 'buchungsdatum', 'Wertst./Fälligkeit': 'wertstellung', 'Abgrenzungsdatum': 'abgrenzung',
  'brutto': 'brutto', 'netto': 'netto', 'USt. %': 'ust_satz',
  'Soll-Kontonr.': 'soll_konto_nr', 'Soll-Konto': 'soll_konto', 'Soll-Kontoart': 'soll_kontoart', 'Soll-Kontokategorie': 'soll_kategorie',
  'Haben-Kontonr.': 'haben_konto_nr', 'Haben-Konto': 'haben_konto', 'Haben-Kontoart': 'haben_kontoart', 'Haben-Kontokategorie': 'haben_kategorie',
  'OP': 'op', 'OP-Betrag': 'op_betrag',
};
const PFLICHT = ['Nummer', 'Buchungstyp', 'Wertst./Fälligkeit', 'brutto', 'Soll-Kontonr.', 'Haben-Kontonr.'];
const DATUM: ReadonlySet<keyof JournalZeile> = new Set(['buchungsdatum', 'wertstellung', 'abgrenzung']);
const ZAHL: ReadonlySet<keyof JournalZeile> = new Set(['netto', 'ust_satz', 'op_betrag']);
export const BANK_KATEGORIEN: ReadonlySet<string> = new Set(['Banken / Kassen', 'Objektbankkonten']);

export function leseJournalText(bytes: ArrayBuffer): string {
  const utf8 = new TextDecoder('utf-8').decode(bytes);
  return (brauchtWindows1252(utf8) ? new TextDecoder('windows-1252').decode(bytes) : utf8).replace(/^﻿/, '');
}

/** CSV mit Semikolon; Anführungszeichen ("" escaped), Zeilenumbrüche innerhalb von Anführungszeichen erlaubt. */
export function zerlegeCsv(text: string): string[][] {
  const zeilen: string[][] = [];
  let feld = ''; let zeile: string[] = []; let inAnf = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inAnf) {
      if (ch === '"' && text[i + 1] === '"') { feld += '"'; i++; }
      else if (ch === '"') inAnf = false;
      else feld += ch;
    } else if (ch === '"') inAnf = true;
    else if (ch === ';') { zeile.push(feld); feld = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      zeile.push(feld); feld = '';
      if (zeile.some((f) => f.trim())) zeilen.push(zeile);
      zeile = [];
    } else feld += ch;
  }
  zeile.push(feld);
  if (zeile.some((f) => f.trim())) zeilen.push(zeile);
  return zeilen;
}

export function deDatum(s: string): string | null {
  const m = (s ?? '').trim().match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : null;
}

export function deZahl(s: string): number | null {
  const t = (s ?? '').trim();
  if (!t) return null;
  const n = Number(t.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

export function parseJournal(text: string): JournalErgebnis {
  const roh = zerlegeCsv(text);
  const fehler: string[] = [];
  if (roh.length < 2) return { zeilen: [], fehler: ['Die Datei enthält keine Buchungen.'], bankkonten: [], von: null, bis: null };
  const kopf = roh[0].map((h) => h.trim());
  const fehlend = PFLICHT.filter((p) => !kopf.includes(p));
  if (fehlend.length) {
    return { zeilen: [], bankkonten: [], von: null, bis: null,
      fehler: [`Das ist kein Immoware-Buchungsjournal – es fehlen die Spalten: ${fehlend.join(', ')}.`] };
  }
  const zeilen: JournalZeile[] = [];
  const bank = new Set<string>();
  roh.slice(1).forEach((werte, i) => {
    const nr = i + 2;
    const z = { zeile: nr, op: null } as unknown as Record<string, unknown>;
    kopf.forEach((h, s) => {
      const feld = SPALTEN[h];
      if (!feld) return;
      const w = (werte[s] ?? '').trim();
      if (DATUM.has(feld)) z[feld] = w ? deDatum(w) : null;
      else if (ZAHL.has(feld)) z[feld] = deZahl(w);
      else if (feld === 'brutto') z[feld] = deZahl(w);
      else if (feld === 'op') z[feld] = w === '' ? null : w === '1';
      else z[feld] = w;
    });
    for (const f of Object.values(SPALTEN)) if (z[f] === undefined) z[f] = ZAHL.has(f) || DATUM.has(f) ? null : '';
    if (z.brutto == null) { fehler.push(`Zeile ${nr}: Betrag „${werte[kopf.indexOf('brutto')] ?? ''}“ nicht lesbar.`); return; }
    if (!z.wertstellung) { fehler.push(`Zeile ${nr}: Wertstellung fehlt oder ist kein Datum.`); return; }
    const zl = z as unknown as JournalZeile;
    if (BANK_KATEGORIEN.has(zl.soll_kategorie)) bank.add(zl.soll_konto);
    if (BANK_KATEGORIEN.has(zl.haben_kategorie)) bank.add(zl.haben_konto);
    zeilen.push(zl);
  });
  const daten = zeilen.map((z) => z.wertstellung!).sort();
  return { zeilen, fehler, bankkonten: [...bank].sort(), von: daten[0] ?? null, bis: daten[daten.length - 1] ?? null };
}
